/**
 * Son tamamlama tarihine 2 gün kalan, henüz tamamlanmamış atamalar için
 * uyarı mesajı gönderir.
 *
 * Uyarı üç tarafa birden gider:
 *   1. Atamanın sahibi çalışan
 *   2. Çalışanın bağlı olduğu SMO Lead (çalıştığı merkezlerin sahibi)
 *   3. Çalışanın merkezlerinin bulunduğu illerin bölge müdürleri
 *
 * Mesajlar projedeki mevcut Messenger altyapısına (`messages` koleksiyonu)
 * yazılır. Mükerrer gönderimi önlemek için her (atama, çalışan) çifti için
 * `assignmentReminders` koleksiyonunda tek bir kayıt tutulur; kaydın id'si
 * deterministiktir, bu yüzden aynı uyarı ikinci kez gönderilemez.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

// Türkçe il adlarını karşılaştırılabilir hale getirir (useAllowedEmployeeIds ile aynı kural)
const normalizeCity = (city) =>
  String(city || '')
    .toLowerCase()
    .trim()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');

/** Firestore doküman id'sinde kullanılamayan karakterleri temizler. */
const safeId = (value) => String(value || '').replace(/[/\\.#$[\]]/g, '_');

const reminderDocId = (assignmentId, employeeId) =>
  `${safeId(assignmentId)}__${safeId(employeeId)}`;

/**
 * Çalışanın bağlı olduğu SMO Lead ve bölge müdürlerinin çalışan id'lerini bulur.
 * Çalışanın projelerdeki merkezleri üzerinden çözülür.
 */
export const resolveSupervisorIds = async (employee) => {
  const centerIds = new Set(
    (employee?.projects || [])
      .map((assignment) => assignment.centerId)
      .filter(Boolean)
  );

  if (centerIds.size === 0) return { smoLeadIds: [], regionManagerIds: [] };

  const [centersSnapshot, regionsSnapshot] = await Promise.all([
    getDocs(collection(db, 'Centers')),
    getDocs(collection(db, 'Regions')),
  ]);

  const smoLeadIds = new Set();
  const myCities = new Set();

  centersSnapshot.docs.forEach((centerDoc) => {
    if (!centerIds.has(centerDoc.id)) return;
    const data = centerDoc.data();
    if (data.SMO) smoLeadIds.add(data.SMO);
    if (data.SMOBackup) smoLeadIds.add(data.SMOBackup);
    if (data['İl']) myCities.add(normalizeCity(data['İl']));
  });

  const regionManagerIds = new Set();
  regionsSnapshot.docs.forEach((regionDoc) => {
    const region = regionDoc.data();
    const cities = region.cities || [];
    const overlaps = cities.some((city) => myCities.has(normalizeCity(city)));
    if (!overlaps) return;

    if (region.managerId) regionManagerIds.add(region.managerId);
    Object.values(region.cityManagerIds || {}).forEach((value) => {
      if (Array.isArray(value)) value.forEach((id) => id && regionManagerIds.add(id));
      else if (value) regionManagerIds.add(value);
    });
  });

  // Çalışanın kendisi yönetici konumundaysa kendine ikinci kez göndermeyelim
  smoLeadIds.delete(employee.id);
  regionManagerIds.delete(employee.id);

  return {
    smoLeadIds: [...smoLeadIds],
    regionManagerIds: [...regionManagerIds],
  };
};

/** Çalışan id listesini e-posta listesine çevirir. */
const resolveEmails = async (employeeIds) => {
  const emails = [];
  await Promise.all(
    employeeIds.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'Employees', id));
        const email = snap.exists() ? snap.data().eMail : null;
        if (email) emails.push(email);
      } catch (error) {
        console.debug('Yönetici e-postası çözülemedi:', id, error);
      }
    })
  );
  return emails;
};

const buildMessage = (assignment, employee, forSelf) => {
  const fullName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();
  const deadline = assignment.deadlineLabel;
  const header = `⚠️ ${assignment.type} son tamamlama uyarısı`;

  if (forSelf) {
    return (
      `${header}\n\n` +
      `"${assignment.name}" atamanız için ${deadline}.\n` +
      `Lütfen Profil sayfanızdan tamamlayın.`
    );
  }

  return (
    `${header}\n\n` +
    `${fullName || 'Bir ekip üyesi'} için "${assignment.name}" ataması ` +
    `henüz tamamlanmadı — ${deadline}.`
  );
};

/**
 * Verilen atamalar için uyarıları gönderir.
 * Daha önce uyarısı gönderilmiş atamalar atlanır.
 *
 * @returns {Promise<number>} gönderilen yeni uyarı sayısı
 */
export const sendDeadlineReminders = async (assignments, employee, senderEmail) => {
  if (!employee?.id || !senderEmail) return 0;

  // Yalnızca son tarihi olan, son 2 güne girmiş atamalar uyarı üretir
  const targets = assignments.filter((item) => item.dueSoon && item.dueDate);
  if (targets.length === 0) return 0;

  // Hangileri için daha önce uyarı gönderilmiş?
  const pending = [];
  await Promise.all(
    targets.map(async (assignment) => {
      const ref = doc(db, 'assignmentReminders', reminderDocId(assignment.id, employee.id));
      try {
        const snap = await getDoc(ref);
        if (!snap.exists()) pending.push({ assignment, ref });
      } catch (error) {
        console.debug('Uyarı kaydı okunamadı:', error);
      }
    })
  );

  if (pending.length === 0) return 0;

  const { smoLeadIds, regionManagerIds } = await resolveSupervisorIds(employee);
  const supervisorEmails = await resolveEmails([...smoLeadIds, ...regionManagerIds]);
  const employeeEmail = employee.eMail;

  let sent = 0;
  for (const { assignment, ref } of pending) {
    try {
      const recipients = [];
      if (employeeEmail) {
        recipients.push({ email: employeeEmail, text: buildMessage(assignment, employee, true) });
      }
      supervisorEmails.forEach((email) => {
        recipients.push({ email, text: buildMessage(assignment, employee, false) });
      });

      if (recipients.length === 0) continue;

      await Promise.all(
        recipients.map((recipient) =>
          addDoc(collection(db, 'messages'), {
            senderEmail,
            receiverEmail: recipient.email,
            text: recipient.text,
            fileUrl: null,
            fileName: null,
            timestamp: serverTimestamp(),
            read: false,
            isDeadlineReminder: true,
          })
        )
      );

      // Kayıt en son yazılır; mesajlar gitmezse bir sonraki denemede tekrarlanır
      await setDoc(ref, {
        assignmentId: assignment.id,
        assignmentType: assignment.type,
        assignmentName: assignment.name,
        employeeId: employee.id,
        dueDate: assignment.dueDate,
        notifiedEmails: recipients.map((r) => r.email),
        createdAt: serverTimestamp(),
      });

      sent += 1;
    } catch (error) {
      console.error('Son tarih uyarısı gönderilemedi:', error);
    }
  }

  return sent;
};

/** Bir atamanın uyarı kaydı var mı? (rapor ekranları için) */
export const fetchSentReminders = async () => {
  const snap = await getDocs(collection(db, 'assignmentReminders'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export { reminderDocId };
